import { useEffect, useState } from "react";
import { HandHeart, Bell, Mail } from "@/lib/icons";
import { toast } from "sonner";
import { api, formatApiError } from "@/lib/api";
import { ModalClose } from "@/components/ui/ModalClose";

export function NudgeModal({ client, onClose, onSent }) {
  const [templates, setTemplates] = useState([]);
  const [pick, setPick] = useState("thinking");
  const [custom, setCustom] = useState("");
  const [push, setPush] = useState(true);
  const [email, setEmail] = useState(false);
  const [busy, setBusy] = useState(false);
  useEffect(() => { api.get("/clinician/nudge-templates").then((r) => setTemplates(r.data.templates)); }, []);
  const first = client.name.split(" ")[0];
  const preview = custom.trim() || templates.find((t) => t.key === pick)?.text || "";

  const send = async () => {
    setBusy(true);
    try {
      const r = await api.post(`/clinician/client/${client.id}/nudge`, { message: custom.trim(), template: pick, push, email });
      toast.success(`Nudge sent to ${first}${r.data.pushed ? " · pushed" : ""}${r.data.emailed ? " · emailed" : ""}`);
      onSent?.(r.data);
      onClose();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); } finally { setBusy(false); }
  };

  return (
    <div className="fixed inset-0 z-[80] flex items-center justify-center bg-stone-900/50 backdrop-blur-sm p-4" data-testid="nudge-modal" onClick={onClose}>
      <div className="warm-card relative p-6 w-full max-w-md space-y-4" onClick={(e) => e.stopPropagation()}>
        <ModalClose onClose={onClose} testid="nudge-close" />
        <div className="flex items-center gap-2 pr-10">
          <HandHeart className="w-5 h-5 text-amber-700" />
          <h3 className="font-display text-lg font-bold text-stone-900">Gentle nudge for {first}</h3>
        </div>
        <p className="text-xs text-stone-500">Shows as a warm card on their Today tab: <em>"{"{you}"} checked in: …"</em>. One per member every 3 days — never a reminder to "catch up".</p>
        <div className="space-y-2">
          {templates.map((t) => (
            <label key={t.key} className={`flex items-start gap-3 rounded-2xl border p-3 cursor-pointer ${pick === t.key && !custom.trim() ? "border-amber-400 bg-amber-50" : "border-stone-200"}`}>
              <input type="radio" name="nudge-template" value={t.key} checked={pick === t.key} onChange={() => { setPick(t.key); setCustom(""); }} data-testid={`nudge-template-${t.key}`} className="mt-1 accent-amber-700" />
              <span className="text-sm text-stone-800">{t.text}</span>
            </label>
          ))}
          <textarea value={custom} onChange={(e) => setCustom(e.target.value)} rows={2} maxLength={400} data-testid="nudge-custom" placeholder="…or write your own (positive, no catch-up pressure)"
            className="w-full rounded-2xl border border-stone-300 px-3 py-2 text-sm outline-none focus:border-amber-600" />
        </div>
        <div className="rounded-2xl bg-stone-50 border border-stone-200 p-3 text-sm text-stone-700" data-testid="nudge-preview"><span className="text-[10px] font-bold uppercase tracking-widest text-amber-800 block mb-1">Preview</span>{preview}</div>
        <div className="flex flex-wrap gap-4 text-sm text-stone-700">
          <label className="inline-flex items-center gap-2"><input type="checkbox" checked={push} onChange={(e) => setPush(e.target.checked)} data-testid="nudge-push" className="accent-amber-700" /><Bell className="w-3.5 h-3.5" /> Push notification</label>
          <label className="inline-flex items-center gap-2"><input type="checkbox" checked={email} onChange={(e) => setEmail(e.target.checked)} data-testid="nudge-email" className="accent-amber-700" /><Mail className="w-3.5 h-3.5" /> Email (generic, no content)</label>
        </div>
        <div className="flex gap-2">
          <button onClick={onClose} data-testid="nudge-cancel" className="flex-1 px-4 py-2.5 rounded-full border border-stone-300 text-sm font-semibold text-stone-700">Cancel</button>
          <button onClick={send} disabled={busy || !preview} data-testid="nudge-send" className="flex-1 amber-btn text-sm">{busy ? "Sending…" : "Send nudge"}</button>
        </div>
      </div>
    </div>
  );
}
